<template>
  <el-card shadow="never" v-loading="loading">
    <div slot="header" class="template-header">
      <span>Email Templates</span>
      <div>
        <el-button size="small" @click="loadTemplates">
          Refresh
        </el-button>
        <el-button size="small" type="primary" @click="openCreateDialog">
          New Template
        </el-button>
      </div>
    </div>

    <el-table :data="templates" empty-text="No templates found" stripe>
      <el-table-column prop="name" label="Name" min-width="180" />
      <el-table-column prop="type" label="Type" width="110" />
      <el-table-column prop="format" label="Format" width="110" />
      <el-table-column prop="key" label="Key" min-width="180" />
      <el-table-column prop="version" label="Version" width="100" />
      <el-table-column label="Status" width="120">
        <template slot-scope="{ row }">
          <el-tag :type="tagType(row.status)" size="small">
            {{ row.status }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Updated" width="180">
        <template slot-scope="{ row }">
          {{ formatDate(row.updatedAt) }}
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="160">
        <template slot-scope="{ row }">
          <div class="template-table-actions">
            <el-button size="mini" @click="openEditDialog(row)">
              Edit
            </el-button>
            <el-button size="mini" @click="openVersionsDialog(row)">
              Versions
            </el-button>
            <el-button size="mini" @click="previewStoredTemplate(row)">
              Preview
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      class="template-dialog"
      width="72%"
      top="2vh"
      destroy-on-close
    >
      <el-form label-position="top" class="template-form">
        <el-row :gutter="16">
          <el-col :span="7">
            <el-form-item label="Name">
              <el-input v-model="form.name" />
            </el-form-item>
          </el-col>
          <el-col :span="7">
            <el-form-item label="Key">
              <el-input
                v-model="form.key"
                placeholder="customer-welcome"
                :disabled="isEditing"
              />
            </el-form-item>
          </el-col>
          <el-col :span="4">
            <el-form-item label="Type">
              <el-select v-model="form.type" style="width: 100%;" :disabled="isEditing">
                <el-option label="Template" value="general" />
                <el-option label="Header" value="header" />
                <el-option label="Footer" value="footer" />
                <el-option
                  v-if="isLegacyTemplateType"
                  :label="`Template (${form.type})`"
                  :value="form.type"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="3">
            <el-form-item label="Status">
              <el-select v-model="form.status" style="width: 100%;">
                <el-option label="Draft" value="draft" />
                <el-option label="Published" value="published" />
                <el-option label="Archived" value="archived" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="3">
            <el-form-item label="Format">
              <el-select v-model="form.format" style="width: 100%;">
                <el-option label="HTML" value="html" />
                <el-option label="MJML" value="mjml" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-alert
          v-if="isEditing"
          :closable="false"
          type="info"
          title="Saving changes creates a new template version and preserves the current one."
          class="template-version-alert"
        />

        <el-form-item label="Description">
          <el-input v-model="form.description" type="textarea" :rows="2" />
        </el-form-item>

        <el-form-item v-if="requiresSubject" label="Subject">
          <el-input v-model="form.subject" />
        </el-form-item>

        <el-divider>Body</el-divider>
        <div v-if="isHtmlTemplate" class="template-body-actions">
          <el-button
            size="small"
            plain
            @click="triggerHtmlImport"
          >
            Import HTML
          </el-button>
          <span class="template-body-hint">
            Paste HTML directly or import a `.html` file.
          </span>
        </div>
        <input
          ref="htmlFileInput"
          type="file"
          accept=".html,.htm,text/html"
          class="template-file-input"
          @change="handleHtmlFileImport"
        />
        <EmailEditor
          v-model="form.body"
          :title="editorTitle"
          :preview="false"
          :template-properties="propertyOptions"
          :format="form.format"
        />


        <el-alert
          v-if="missingSelectedPropertyKeys.length"
          :closable="false"
          type="error"
          :title="`Missing variable definitions: ${missingSelectedPropertyKeys.join(', ')}`"
          class="template-property-alert"
        />

        <el-divider />

        <div class="template-actions">
          <el-button @click="previewTemplate" :loading="previewLoading">
            Preview
          </el-button>
          <el-button type="primary" @click="saveTemplate" :loading="saving">
            Save
          </el-button>
        </div>

        <el-card v-if="previewBody || previewSubject" shadow="never" class="template-preview">
          <div slot="header">
            <strong>Preview</strong>
          </div>
          <p><strong>Subject:</strong> {{ previewSubject || 'No subject' }}</p>
          <iframe
            v-if="previewBody"
            class="template-preview-frame"
            :srcdoc="previewBody"
            sandbox=""
            title="Template Preview"
          ></iframe>
        </el-card>
      </el-form>
    </el-dialog>

    <el-dialog
      title="Template Versions"
      :visible.sync="versionsDialogVisible"
      width="70%"
      destroy-on-close
    >
      <el-table :data="templateVersions" stripe empty-text="No versions found">
        <el-table-column prop="version" label="Version" width="100" />
        <el-table-column label="Status" width="120">
          <template slot-scope="{ row }">
            <el-tag :type="tagType(row.status)" size="small">
              {{ row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Latest" width="100">
          <template slot-scope="{ row }">
            <el-tag v-if="row.isLatest" size="small" type="success">
              Latest
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Updated" width="180">
          <template slot-scope="{ row }">
            {{ formatDate(row.updatedAt) }}
          </template>
        </el-table-column>
        <el-table-column prop="updatedBy" label="Updated By" min-width="180" />
        <el-table-column label="Actions" width="120">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="openVersionForEdit(row)">
              Open
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </el-card>
</template>

<script>
import EmailEditor from './EmailEditor';
import {
  createEditorEmailTemplate,
  listEditorEmailTemplateProperties,
  listEditorEmailTemplates,
  listEditorEmailTemplateVersions,
  previewEditorEmailTemplate,
  updateEditorEmailTemplate,
} from '../utils/commonApi';
import { getEmailTemplateContent } from '../utils/emailScaffold';

function createEmptyTemplate() {
  return {
    name: '',
    key: '',
    type: 'general',
    format: 'html',
    subject: '',
    body: '',
    description: '',
    propertyKeys: [],
    status: 'draft',
  };
}

const TEMPLATE_PROPERTY_KEY_PATTERN = /^[a-z0-9._-]+$/;

function normalizeEditorTemplate(template) {
  const { _id: objectId } = template;
  const format = template.format || 'html';

  return {
    ...template,
    id: template.id || objectId || '',
    type: template.type || 'general',
    format,
    body: getEmailTemplateContent(template),
    missingPropertyKeys: Array.isArray(template.missingPropertyKeys)
      ? template.missingPropertyKeys
      : [],
  };
}

function getPropertyFallbackValue(property) {
  if (property.fallbackValue !== undefined) {
    return property.fallbackValue;
  }

  if (property.defaultValue !== undefined) {
    return property.defaultValue;
  }

  return property.sampleValue;
}

function normalizeProperty(property) {
  const { _id: objectId } = property;
  const fallbackValue = getPropertyFallbackValue(property);
  return {
    ...property,
    id: property.id || objectId || '',
    type: ['string', 'number', 'boolean', 'date'].includes(property.type)
      ? property.type
      : 'string',
    fallbackValue: fallbackValue === undefined || fallbackValue === null ? '' : fallbackValue,
    sourceModel: property.sourceModel || 'manual',
    sourcePath: property.sourcePath || '',
  };
}

export default {
  name: 'EmailTemplateManager',
  components: { EmailEditor },
  props: {
    facility: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      saving: false,
      previewLoading: false,
      dialogVisible: false,
      versionsDialogVisible: false,
      isEditing: false,
      form: createEmptyTemplate(),
      templates: [],
      templateVersions: [],
      availableProperties: [],
      previewSubject: '',
      previewBody: '',
    };
  },
  computed: {
    dialogTitle() {
      return this.isEditing ? 'Edit Email Template' : 'Create Email Template';
    },
    propertyOptions() {
      return this.availableProperties.filter(property => property.status !== 'archived');
    },
    selectedProperties() {
      return this.extractDerivedPropertyKeys()
        .map(key => this.propertyOptions.find(property => property.key === key))
        .filter(Boolean);
    },
    missingSelectedPropertyKeys() {
      const availablePropertyKeys = new Set(
        this.propertyOptions.map(property => property.key),
      );

      return this.extractDerivedPropertyKeys()
        .filter(key => TEMPLATE_PROPERTY_KEY_PATTERN.test(key))
        .filter(key => !availablePropertyKeys.has(key));
    },
    invalidSelectedPropertyKeys() {
      return this.extractDerivedPropertyKeys()
        .filter(key => !TEMPLATE_PROPERTY_KEY_PATTERN.test(key));
    },
    isHtmlTemplate() {
      return String(this.form.format || 'html').toLowerCase() === 'html';
    },
    requiresSubject() {
      return this.form.type !== 'header' && this.form.type !== 'footer';
    },
    isLegacyTemplateType() {
      return this.form.type &&
        !['general', 'header', 'footer'].includes(this.form.type);
    },
    editorTitle() {
      if (this.form.type === 'header') {
        return 'Header body';
      }

      if (this.form.type === 'footer') {
        return 'Footer body';
      }

      return 'Template body';
    },
  },
  async mounted() {
    await this.loadProperties();
    await this.loadTemplates();
  },
  methods: {
    tagType(status) {
      if (status === 'published') {
        return 'success';
      }

      if (status === 'archived') {
        return 'info';
      }

      return 'warning';
    },
    formatDate(value) {
      if (!value) {
        return '';
      }

      return new Date(value).toLocaleString();
    },
    async loadTemplates() {
      try {
        this.loading = true;
        const templates = await listEditorEmailTemplates(this.$keycloak.token, {
          includeArchived: true,
        });
        this.templates = templates.map(normalizeEditorTemplate);
      } catch (err) {
        this.$message.error({
          title: 'Unable to load templates',
          message: err.message,
        });
      } finally {
        this.loading = false;
      }
    },
    async loadProperties() {
      try {
        const properties = await listEditorEmailTemplateProperties(this.$keycloak.token, {
          includeArchived: true,
        });
        this.availableProperties = properties.map(normalizeProperty);
      } catch (err) {
        this.$message.error({
          title: 'Unable to load properties',
          message: err.message,
        });
      }
    },
    openCreateDialog() {
      this.isEditing = false;
      this.previewSubject = '';
      this.previewBody = '';
      this.form = createEmptyTemplate();
      this.dialogVisible = true;
    },
    openEditDialog(template) {
      this.isEditing = true;
      this.previewSubject = '';
      this.previewBody = '';
      this.form = {
        ...JSON.parse(JSON.stringify(template)),
        type: template.type || 'general',
        format: template.format || 'html',
        body: getEmailTemplateContent(template),
        propertyKeys: Array.isArray(template.propertyKeys)
          ? template.propertyKeys
          : (template.properties || []).map(property => property.key),
      };
      this.dialogVisible = true;
    },
    async openVersionsDialog(template) {
      try {
        this.templateVersions = await listEditorEmailTemplateVersions(
          template.id,
          this.$keycloak.token,
        );
        this.versionsDialogVisible = true;
      } catch (err) {
        this.$message.error({
          title: 'Unable to load template versions',
          message: err.message,
        });
      }
    },
    openVersionForEdit(template) {
      this.versionsDialogVisible = false;
      this.openEditDialog(template);
    },
    async previewStoredTemplate(template) {
      this.openEditDialog(template);
      await this.previewTemplate();
    },
    triggerHtmlImport() {
      const input = this.$refs.htmlFileInput;

      if (!input) {
        return;
      }

      input.value = '';
      input.click();
    },
    handleHtmlFileImport(event) {
      const input = event && event.target;
      const file = input && input.files && input.files[0];

      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        this.form.body = typeof reader.result === 'string' ? reader.result : '';
        this.$message.success({
          title: 'HTML imported',
        });
      };
      reader.onerror = () => {
        this.$message.error({
          title: 'Unable to import HTML',
          message: 'The selected file could not be read.',
        });
      };
      reader.readAsText(file);
    },
    setPreviewContextValue(context, sourceModel, sourcePath, value) {
      if (!sourceModel || sourceModel === 'manual' || !sourcePath) {
        return context;
      }

      const root = {
        ...(context[sourceModel] && typeof context[sourceModel] === 'object'
          ? context[sourceModel]
          : {}),
      };
      const segments = sourcePath.split('.').filter(Boolean);
      let cursor = root;

      segments.forEach((segment, index) => {
        if (index === segments.length - 1) {
          cursor[segment] = value;
          return;
        }

        cursor[segment] = cursor[segment] && typeof cursor[segment] === 'object'
          ? cursor[segment]
          : {};
        cursor = cursor[segment];
      });

      return {
        ...context,
        [sourceModel]: root,
      };
    },
    buildPreviewContext() {
      return this.selectedProperties.reduce((context, property) => (
        this.setPreviewContextValue(
          context,
          property.sourceModel,
          property.sourcePath,
          property.fallbackValue || '',
        )
      ), {});
    },
    buildPreviewValues() {
      return this.selectedProperties.reduce((values, property) => {
        if (property.sourceModel && property.sourceModel !== 'manual') {
          return values;
        }

        const nextValue = property.fallbackValue || '';
        return {
          ...values,
          [property.key]: nextValue,
        };
      }, {});
    },
    extractDerivedPropertyKeys() {
      const keys = [];
      const seenKeys = new Set();
      const tokenPattern = /{{{\s*([^{}]+?)\s*}}}|{{\s*([^{}]+?)\s*}}/g;

      const templateParts = this.requiresSubject
        ? [this.form.subject, this.form.body]
        : [this.form.body];

      templateParts.forEach((part) => {
        if (typeof part !== 'string' || !part.length) {
          return;
        }

        let match = tokenPattern.exec(part);

        while (match) {
          const key = String(match[1] || match[2] || '').trim().toLowerCase();
          if (key && !seenKeys.has(key)) {
            seenKeys.add(key);
            keys.push(key);
          }
          match = tokenPattern.exec(part);
        }

        tokenPattern.lastIndex = 0;
      });

      return keys;
    },
    async previewTemplate() {
      if (this.invalidSelectedPropertyKeys.length) {
        this.$message.error({
          title: 'Invalid template variables',
          message: `Invalid variable keys: ${this.invalidSelectedPropertyKeys.join(', ')}`,
        });
        return;
      }

      if (this.missingSelectedPropertyKeys.length) {
        this.$message.error({
          title: 'Missing template variables',
          message: `ManagedEmailTemplateProperty not found for keys: ${this.missingSelectedPropertyKeys.join(', ')}`,
        });
        return;
      }

      try {
        this.previewLoading = true;
        const preview = await previewEditorEmailTemplate({
          type: this.form.type,
          format: this.form.format,
          subject: this.requiresSubject ? this.form.subject : '',
          body: this.form.body,
          propertyKeys: this.extractDerivedPropertyKeys(),
          values: this.buildPreviewValues(),
          context: this.buildPreviewContext(),
        }, this.$keycloak.token);

        this.previewSubject = preview.subject;
        this.previewBody = preview.body;
      } catch (err) {
        this.$message.error({
          title: 'Unable to preview template',
          message: err.message,
        });
      } finally {
        this.previewLoading = false;
      }
    },
    async saveTemplate() {
      if (!this.form.name) {
        this.$message.warning({
          title: 'Missing fields',
          message: 'Name is required.',
        });
        return;
      }

      if (this.form.status === 'published' && this.requiresSubject && !this.form.subject) {
        this.$message.warning({
          title: 'Missing fields',
          message: 'Subject is required before publishing.',
        });
        return;
      }

      if (this.form.status === 'published' && !this.form.body) {
        this.$message.warning({
          title: 'Missing fields',
          message: 'Body is required before publishing.',
        });
        return;
      }

      if (this.invalidSelectedPropertyKeys.length) {
        this.$message.error({
          title: 'Invalid template variables',
          message: `Invalid variable keys: ${this.invalidSelectedPropertyKeys.join(', ')}`,
        });
        return;
      }

      if (this.missingSelectedPropertyKeys.length) {
        this.$message.error({
          title: 'Missing template variables',
          message: `ManagedEmailTemplateProperty not found for keys: ${this.missingSelectedPropertyKeys.join(', ')}`,
        });
        return;
      }

      try {
        this.saving = true;
        const payload = {
          name: this.form.name,
          key: this.form.key,
          type: this.form.type,
          format: this.form.format,
          subject: this.requiresSubject ? this.form.subject : '',
          body: this.form.body,
          description: this.form.description,
          status: this.form.status,
          propertyKeys: this.extractDerivedPropertyKeys(),
        };
        if (this.isEditing && this.form.id) {
          await updateEditorEmailTemplate(this.form.id, payload, this.$keycloak.token);
        } else {
          await createEditorEmailTemplate(payload, this.$keycloak.token);
        }

        this.$message.success({
          title: 'Template saved',
        });
        this.dialogVisible = false;
        await this.loadTemplates();
        this.$emit('templates-updated');
      } catch (err) {
        this.$message.error({
          title: 'Unable to save template',
          message: err.message,
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.template-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.template-property {
  margin-top: 12px;
}

.template-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.template-dialog >>> .el-dialog__body {
  padding: 20px 24px 24px;
}

.template-form {
  margin-top: 4px;
}

.template-form >>> .el-form-item {
  margin-bottom: 14px;
}

.template-form >>> .el-form-item__label {
  padding: 0 0 6px;
  line-height: 1.2;
  text-align: left;
}

.template-form >>> .el-divider--horizontal {
  margin: 18px 0;
}

.template-body-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.template-body-hint {
  color: #909399;
  font-size: 12px;
}

.template-file-input {
  display: none;
}

.template-table-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.template-table-actions .el-button + .el-button {
  margin-left: 0;
}

.template-table-actions .el-button {
  min-width: 100px;
}

.template-version-alert {
  margin-bottom: 16px;
}

.template-preview {
  margin-top: 16px;
}

.template-preview-frame {
  width: 100%;
  min-height: 420px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
}

.template-property-alert {
  margin-bottom: 16px;
}
</style>



// WEBPACK FOOTER //
// src/components/EmailTemplateManager.vue